import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Send,
  Loader,
  Wifi,
  WifiOff,
  RefreshCw,
  Trash2,
  History,
} from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import ChatIcon from "../../../components/Common/ChatIcon";
import useIncidentChat from "../../../hooks/useIncidentChat";
import useTagNotifications from "../../../hooks/useTagNotifications";
import MentionDropdown from "../../../components/Common/MentionDropdown";

/**
 * Get initials from a name
 * @param {string} name - User name
 * @returns {string} Initials (max 2 characters)
 */
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Generate a consistent color based on user ID or name
 * @param {string} str - User ID or name
 * @returns {string} HSL color string
 */
const getAvatarColor = (str) => {
  if (!str) return "hsl(220, 70%, 50%)";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 65%, 45%)`;
};

/**
 * User Avatar Component with initials or image
 */
const UserAvatar = ({ userName, userImageUrl, userId, isCurrentUser }) => {
  const initials = getInitials(userName);
  const bgColor = isCurrentUser ? "#3b82f6" : getAvatarColor(userId || userName);

  if (userImageUrl) {
    return (
      <div className="chatbot__avatar">
        <img
          src={userImageUrl}
          alt={userName || "User"}
          className="chatbot__avatar-image"
        />
      </div>
    );
  }

  return (
    <div
      className="chatbot__avatar"
      style={{ backgroundColor: bgColor }}
    >
      <span className="chatbot__avatar-initials">{initials}</span>
    </div>
  );
};

/**
 * Message Tooltip Component
 */
const MessageTooltip = ({ userName, timestamp, isCurrentUser }) => {
  const date = new Date(timestamp);
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className={`chatbot__tooltip ${isCurrentUser ? "chatbot__tooltip--right" : "chatbot__tooltip--left"}`}>
      <div className="chatbot__tooltip-name">{userName || "Unknown User"}</div>
      <div className="chatbot__tooltip-time">{formattedDate} at {formattedTime}</div>
    </div>
  );
};

export default function Chatbot({ incident }) {
  const { user } = useUser();
  const currentUserId = user?.id;

  const {
    messages,
    connectionState,
    error,
    isSending,
    isLoadingHistory,
    sendMessage,
    clearMessages,
    reloadHistory,
    retryConnection,
    isConnected,
  } = useIncidentChat(incident);

  const { sendTagNotification } = useTagNotifications();

  const [inputValue, setInputValue] = useState("");
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Mention dropdown state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchText, setMentionSearchText] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [taggedClients, setTaggedClients] = useState([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending || !isConnected) return;

    const message = inputValue;
    const clientsToNotify = [...taggedClients];
    
    setInputValue("");
    setTaggedClients([]);
    
    await sendMessage(message);

    // Send tag notifications to all tagged clients
    for (const client of clientsToNotify) {
      await sendTagNotification({
        targetClientId: client.id,
        targetClientName: client.name,
        sourceType: "incident",
        sourceId: incident.id,
        sourceTitle: incident.title,
        message: message,
      });
    }
  };

  const handleKeyPress = (e) => {
    // Don't send if mention dropdown is open (let it handle Enter)
    if (showMentionDropdown) return;
    
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle input change and detect @ mentions
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    setInputValue(value);

    // Find the @ symbol before the cursor
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    
    if (lastAtIndex !== -1) {
      // Check if there's a space after the @ (completed mention)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // If no space after @, show dropdown
      if (!textAfterAt.includes(" ")) {
        setMentionStartIndex(lastAtIndex);
        setMentionSearchText(textAfterAt);
        
        // Calculate dropdown position
        if (textareaRef.current) {
          const rect = textareaRef.current.getBoundingClientRect();
          setMentionPosition({
            top: -180, // Position above the input
            left: 0,
          });
        }
        
        setShowMentionDropdown(true);
        return;
      }
    }
    
    setShowMentionDropdown(false);
  }, []);

  // Handle client selection from mention dropdown
  const handleMentionSelect = useCallback((client) => {
    if (mentionStartIndex === -1) return;

    // Replace the @searchText with @ClientName
    const beforeMention = inputValue.substring(0, mentionStartIndex);
    const afterMention = inputValue.substring(
      mentionStartIndex + 1 + mentionSearchText.length
    );
    const newValue = `${beforeMention}@${client.name} ${afterMention}`;
    
    setInputValue(newValue);
    setShowMentionDropdown(false);
    setMentionStartIndex(-1);
    setMentionSearchText("");
    
    // Add to tagged clients if not already tagged
    if (!taggedClients.find(c => c.id === client.id)) {
      setTaggedClients(prev => [...prev, client]);
    }
    
    // Focus back on textarea
    textareaRef.current?.focus();
  }, [inputValue, mentionStartIndex, mentionSearchText, taggedClients]);

  // Close mention dropdown
  const handleMentionClose = useCallback(() => {
    setShowMentionDropdown(false);
    setMentionStartIndex(-1);
    setMentionSearchText("");
  }, []);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getConnectionStatusDisplay = () => {
    switch (connectionState) {
      case "connected":
        return {
          icon: <Wifi size={12} />,
          text: "Connected",
          className: "chatbot__status--connected",
        };
      case "connecting":
        return {
          icon: <Loader size={12} className="spinning" />,
          text: "Connecting...",
          className: "chatbot__status--connecting",
        };
      case "reconnecting":
        return {
          icon: <RefreshCw size={12} className="spinning" />,
          text: "Reconnecting...",
          className: "chatbot__status--reconnecting",
        };
      case "disconnected":
      default:
        return {
          icon: <WifiOff size={12} />,
          text: "Offline",
          className: "chatbot__status--disconnected",
        };
    }
  };

  /**
   * Check if a message is from the current user
   */
  const isOwnMessage = (message) => {
    // If message has userId, compare with current user
    if (message.userId && currentUserId) {
      return message.userId === currentUserId;
    }
    // If message is local (just sent), it's from current user
    if (message.isLocal) {
      return true;
    }
    return false;
  };

  const connectionStatus = getConnectionStatusDisplay();

  return (
    <div className="chatbot">
      <div className="chatbot__header">
        <div className="chatbot__header-content">
          <ChatIcon size={20} className="chatbot__header-icon" />
          <div className="chatbot__header-text">
            <h3 className="chatbot__header-title">Incident Chat</h3>
            <p className="chatbot__header-subtitle">
              Collaborate on this incident
            </p>
          </div>
        </div>
        <div className="chatbot__header-actions">
          <div className={`chatbot__status ${connectionStatus.className}`}>
            {connectionStatus.icon}
            <span>{connectionStatus.text}</span>
          </div>
          {connectionState === "disconnected" && (
            <button
              className="chatbot__action-btn"
              onClick={retryConnection}
              title="Retry connection"
            >
              <RefreshCw size={14} />
            </button>
          )}
          {/* <button
            className="chatbot__action-btn"
            onClick={reloadHistory}
            disabled={isLoadingHistory}
            title="Reload chat history"
          >
            <History size={14} className={isLoadingHistory ? "spinning" : ""} />
          </button> */}
          {/* <button
            className="chatbot__action-btn"
            onClick={clearMessages}
            title="Clear chat"
          >
            <Trash2 size={14} />
          </button> */}
        </div>
      </div>

      {error && (
        <div className="chatbot__error">
          <span>{error}</span>
          <button onClick={retryConnection} className="chatbot__error-retry">
            Retry
          </button>
        </div>
      )}

      <div className="chatbot__messages">
        {isLoadingHistory && (
          <div className="chatbot__loading-history">
            <Loader size={20} className="spinning" />
            <span>Loading chat history...</span>
          </div>
        )}

        {!isLoadingHistory && messages.length === 0 && (
          <div className="chatbot__empty">
            <ChatIcon size={32} className="chatbot__empty-icon" />
            <p>No messages yet. Start the conversation!</p>
          </div>
        )}

        {!isLoadingHistory &&
          messages.map((message) => {
            const isOwn = isOwnMessage(message);
            const displayName = isOwn
              ? user?.fullName || user?.firstName || "You"
              : message.userName || "User";
            const displayImage = isOwn ? user?.imageUrl : message.userImageUrl;

            return (
              <div
                key={message.id}
                className={`chatbot__message ${isOwn ? "chatbot__message--own" : "chatbot__message--other"} ${
                  message.isError ? "chatbot__message--error" : ""
                } ${message.isLocal ? "chatbot__message--sending" : ""}`}
                onMouseEnter={() => setHoveredMessageId(message.id)}
                onMouseLeave={() => setHoveredMessageId(null)}
              >
                <div
                  className="chatbot__avatar-wrapper"
                  title={displayName}
                >
                  <UserAvatar
                    userName={displayName}
                    userImageUrl={displayImage}
                    userId={message.userId}
                    isCurrentUser={isOwn}
                  />
                  {hoveredMessageId === message.id && (
                    <MessageTooltip
                      userName={displayName}
                      timestamp={message.timestamp}
                      isCurrentUser={isOwn}
                    />
                  )}
                </div>
                <div className="chatbot__message-bubble">
                  <div className="chatbot__message-text">{message.content}</div>
                  <div className="chatbot__message-meta">
                    <span className="chatbot__message-time">
                      {formatTime(message.timestamp)}
                    </span>
                    {message.isLocal && (
                      <span className="chatbot__message-status">
                        <Loader size={10} className="spinning" />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot__input">
        {/* Tagged clients indicator */}
        {taggedClients.length > 0 && (
          <div className="chatbot__tagged-clients">
            {taggedClients.map(client => (
              <span key={client.id} className="chatbot__tagged-client">
                @{client.name}
                <button 
                  onClick={() => setTaggedClients(prev => prev.filter(c => c.id !== client.id))}
                  className="chatbot__tagged-client-remove"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        
        <div className="chatbot__input-wrapper">
          {/* Mention Dropdown */}
          <MentionDropdown
            isVisible={showMentionDropdown}
            searchText={mentionSearchText}
            position={mentionPosition}
            onSelect={handleMentionSelect}
            onClose={handleMentionClose}
          />
          
          <textarea
            ref={textareaRef}
            className="chatbot__input-field"
            placeholder={
              isConnected ? "Type @ to mention a client..." : "Connect to send messages..."
            }
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            rows={1}
            disabled={isSending || !isConnected}
          />
          <button
            className="chatbot__send-btn"
            onClick={handleSend}
            disabled={!inputValue.trim() || isSending || !isConnected}
            title="Send message"
          >
            {isSending ? (
              <Loader size={18} className="spinning" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
